// src/pages/moviment/MovimentPage.tsx
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { FiEdit, FiRepeat, FiTrash2 } from "react-icons/fi";
import { IoEnter } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { MovimentInterface } from "../../common/interfaces";
import CreateButton from "../../components/CreateButton";
import CustomTooltip from "../../components/CustomTooltip";
import InfoBanner from "../../components/InfoBanner";
import HelpVideoButton from "../../components/HelpVideoButton";
import PaginationControls from "../../components/PaginationControls";
import { ApprovalStatusLabel } from "../../components/enum/approvalStatusEnum";
import {
  MovimentStatusLabel,
  TypeMovimentStatus
} from "../../components/enum/movimentStatusEnum";
import MovimentCreateModal from "../../components/modal/MovimentCreateModal";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  ActionButton,
  ActionsCell,
  Container,
  SchedulingSearchCard as MovimentSearchCard,
  StatusApprovalCell,
  StatusMovimentCell,
  Table,
  TableContainer,
  TextFeldClientSearch,
  Title
} from "./moviment.styled";

export const MovimentPage = () => {
  const [search, setSearch] = useState("");
  const [moviments, setMoviments] = useState<MovimentInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // dialog de exclusão
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDeleteId, setToDeleteId] = useState<number | null>(null);
  const [toDeleteLabel, setToDeleteLabel] = useState<string>("");

  // modal de criação/edição
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMoviment, setSelectedMoviment] =
    useState<MovimentInterface | null>(null);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [movimentStatus, setMovimentStatus] = useState<TypeMovimentStatus>(
    TypeMovimentStatus.NONE
  );

  const containerId = "toaster-moviment-page";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const { isAdmin, isUser } = useAuth();
  const navigate = useNavigate();

  // busca movimentos
  const fetchMoviments = async (currentPage: number) => {
    try {
      const response = await api.get(`/moviment`, {
        params: {
          page: currentPage,
          limit: 8,
          search: search?.trim() || undefined,
          initial_date: startDate || undefined,
          final_date: endDate || undefined,
          moviment_status:
            movimentStatus !== TypeMovimentStatus.NONE
              ? movimentStatus
              : undefined
        }
      });
      setMoviments(response.data.items);
      setTotalPages(response.data.meta.totalPages);
    } catch (err) {
      console.error("Erro ao buscar movimentações:", err);
    }
  };

  useEffect(() => {
    fetchMoviments(page);
  }, [page, search, startDate, endDate, movimentStatus]);

  const openDeleteDialog = (mov: MovimentInterface) => {
    setToDeleteId(mov.moviment_id);
    setToDeleteLabel(
      mov.tools
        ? mov.tools.tools_name
        : mov.product
          ? mov.product.product_name
          : "Movimentação"
    );
    setDialogOpen(true);
  };
  const cancelDelete = () => {
    setDialogOpen(false);
    setToDeleteId(null);
  };
  const confirmDelete = async () => {
    if (!toDeleteId) return;
    try {
      await api.delete(`/moviment/${toDeleteId}`);
      autoSuccess("Movimentação excluída com sucesso!");
      fetchMoviments(page);
    } catch (err) {
      console.error(err);
      handleHttpError(err);
    } finally {
      cancelDelete();
    }
  };

  const handleOpenCreate = () => {
    setSelectedMoviment(null);
    setIsModalOpen(true);
  };
  const handleOpenEdit = (mov: MovimentInterface) => {
    setSelectedMoviment(mov);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMoviment(null);
    fetchMoviments(page);
  };

  useEffect(() => {
    fetchMoviments(page);
  }, [page, search]);

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <Title>
        Lista de Movimentações
        <CreateButton onClick={handleOpenCreate} style={{ marginLeft: 14 }}>
          <FiRepeat size={18} /> Criar Movimentação
        </CreateButton>
        <HelpVideoButton
          youtubeUrl="https://youtu.be/UZUHONz6nPM"
          modalTitle="Gerenciar Movimentações"
          sx={{
            position: "absolute",
            top: 0,
            right: 8,
            zIndex: 2
          }}
        />
      </Title>

      <InfoBanner icon={<FiRepeat size={26} />} title="Movimentação">
        Histórico de saída e devolução de produtos e ferramentas vinculados a
        uma ordem de serviço — o que foi retirado do estoque/ativos, para
        qual OS, e o que já voltou.
      </InfoBanner>

      <MovimentSearchCard>
        <TextField
          label="Data de Início"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: new Date().toLocaleDateString("en-CA") // impede datas futuras no fuso local
          }}
        />

        <TextField
          disabled={!startDate}
          label="Data Final"
          type="date"
          value={startDate ? endDate : ""}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: startDate || undefined, // impede menor que startDate
            max: new Date().toLocaleDateString("en-CA") // impede datas futuras
          }}
        />

        <TextFeldClientSearch
          label="Dados da Movimentação"
          variant="outlined"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 300 }}
        />

        <FormControl style={{ minWidth: 200 }}>
          <InputLabel id="label-status">Status</InputLabel>
          <Select
            labelId="label-status"
            value={movimentStatus}
            label="Status"
            onChange={(e) =>
              setMovimentStatus(e.target.value as TypeMovimentStatus)
            }
          >
            {(Object.values(TypeMovimentStatus) as TypeMovimentStatus[])
              .filter((code) => code !== TypeMovimentStatus.NONE)
              .map((statusCode) => (
                <MenuItem key={statusCode} value={statusCode}>
                  {MovimentStatusLabel[statusCode]}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </MovimentSearchCard>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Data Inicial</th>
              {/* <th>Termino</th> */}
              <th>Motivo</th>
              {/* <th>Descrição</th> */}
              <th>Status</th>
              <th>Aprovação</th>
              <th>Aprovador</th>
              <th>Cliente</th>
              {isAdmin && <th>Empresa</th>}
              {/* <th>Ativo / Produto</th> */}
              {/* <th>Qtd.</th> */}
              {/* <th>Devolver?</th> */}
              {isAdmin && <th>Usuário</th>}
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {moviments.map((mov) => {
              const dt = new Date(mov.start_date);
              const date = dt.toLocaleDateString("pt-BR");
              const time = dt.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit"
              });

              const start_date = `${date}`;

              let end_date = "-";

              if (mov.end_date) {
                const endDt = new Date(mov.end_date);
                const endDate = endDt.toLocaleDateString("pt-BR");
                const endTime = endDt.toLocaleTimeString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit"
                });

                end_date = `${endDate} ${endTime}`;
              } else {
                end_date = "-";
              }

              const itemName = mov.tools
                ? mov.tools.tools_name
                : mov.product
                  ? mov.product.product_name
                  : "-";
              const quantity = mov.tools
                ? mov.tools_quantity
                : mov.product
                  ? mov.product_quantity
                  : 0;
              const toReturn = mov.tools
                ? mov.tools_return_status === 1
                : mov.product
                  ? mov.product_return_status === 1
                  : false;

              return (
                <tr key={mov.moviment_id}>
                  <td>{start_date}</td>
                  {/* <td>{end_date}</td> */}
                  <td>{mov.moviment_reason ?? "-"}</td>
                  {/* <td>{mov.moviment_description}</td> */}
                  <StatusMovimentCell status={mov.moviment_status}>
                    {MovimentStatusLabel[mov.moviment_status]}
                  </StatusMovimentCell>
                  <StatusApprovalCell status={mov.approval_status}>
                    {ApprovalStatusLabel[mov.approval_status]}
                  </StatusApprovalCell>
                  <td>{mov.approver?.user_name}</td>
                  <td>{mov.client.client_name}</td>
                  {isAdmin && <td>{mov.company.company_name}</td>}
                  {/* <td>{itemName}</td> */}
                  {/* <td>{quantity}</td> */}
                  {/* <td>{toReturn ? 'Sim' : 'Não'}</td> */}
                  {isAdmin && <td>{mov.user?.user_name}</td>}
                  <ActionsCell>
                    {!isUser && (
                      <CustomTooltip title="Editar Movimentação">
                        <ActionButton onClick={() => handleOpenEdit(mov)}>
                          <FiEdit size={20} color="#4caf50" />
                        </ActionButton>
                      </CustomTooltip>
                    )}

                    <CustomTooltip title="Mostrar Detalhes">
                      <ActionButton
                        onClick={() =>
                          navigate(`/moviment-details/${mov.moviment_id}`)
                        }
                        style={{ marginRight: "10px" }}
                      >
                        <IoEnter size={25} color="#007bff" />
                      </ActionButton>
                    </CustomTooltip>

                    {!isUser && (
                      <CustomTooltip title="Excluir Movimentação">
                        <ActionButton onClick={() => openDeleteDialog(mov)}>
                          <FiTrash2 size={20} color="#f44336" />
                        </ActionButton>
                      </CustomTooltip>
                    )}
                  </ActionsCell>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <Dialog open={dialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja realmente excluir a movimentação de{" "}
            <strong>{toDeleteLabel}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancelar</Button>
          <Button onClick={confirmDelete} variant="contained" color="primary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <MovimentCreateModal
        open={isModalOpen}
        onClose={handleCloseModal}
        moviments={selectedMoviment}
      />
    </Container>
  );
};
